/**
 * Typed fetch wrapper. Access JWT is held in memory only.
 * The refresh-token cookie is sent automatically by the browser.
 */

import { toastBus } from "@/components/ui/Toast";

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1";

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(t: string | null) {
  accessToken = t;
}
export function getAccessToken() {
  return accessToken;
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> };
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: Record<string, unknown>;
  constructor(status: number, body: ApiErrorBody | { error?: { message?: string } } | null) {
    const message = (body as ApiErrorBody | null)?.error?.message ?? `HTTP ${status}`;
    super(message);
    this.status = status;
    this.code = (body as ApiErrorBody | null)?.error?.code ?? "error";
    this.details = (body as ApiErrorBody | null)?.error?.details;
  }
}

async function doFetch(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (accessToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (res.status === 401 && retry && !path.startsWith("/auth/")) {
    const ok = await refreshAccess();
    if (ok) return doFetch(path, init, false);
  }
  return res;
}

async function refreshAccess(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, { method: "POST", credentials: "include" });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string };
      setAccessToken(data.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await doFetch(path, init);
  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try { body = (await res.json()) as ApiErrorBody; } catch { /* ignore */ }
    throw new ApiError(res.status, body);
  }
  const ct = res.headers.get("Content-Type") ?? "";
  if (ct.includes("application/json")) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}


/**
 * Mutation options: suppress toast or override the success label.
 * The toast bus is imported lazily so this module stays
 * framework-agnostic (no React import at module top).
 */
interface MutateOpts { silent?: boolean; successMessage?: string }

async function withToast<T>(p: Promise<T>, opts: MutateOpts | undefined): Promise<T> {
  try {
    const result = await p;
    if (!opts?.silent) {
      toastBus.emit("success", opts?.successMessage ?? "Gespeichert.");
    }
    return result;
  } catch (err) {
    if (!opts?.silent) {
      const msg = err instanceof ApiError && err.message ? err.message : "Aktion fehlgeschlagen.";
      toastBus.emit("error", msg);
    }
    throw err;
  }
}

export const apiGet = <T>(p: string) => api<T>(p);
export const apiPost = <T>(p: string, body?: unknown, opts?: MutateOpts) =>
  withToast(api<T>(p, { method: "POST", body: body == null ? undefined : JSON.stringify(body) }), opts);
export const apiPut = <T>(p: string, body?: unknown, opts?: MutateOpts) =>
  withToast(api<T>(p, { method: "PUT", body: body == null ? undefined : JSON.stringify(body) }), opts);
export const apiDelete = <T>(p: string, opts?: MutateOpts) =>
  withToast(api<T>(p, { method: "DELETE" }), opts);
